import React, { useState, useEffect } from 'react';
import { 
  DragAndDropContainer, 
  DragAndDropContent, 
  FileName,
  FileType,
  ButtonContainer
} from './DragAndDrop.styled';
import { RoundedButton } from '../../../../../elements';
import { API } from 'aws-amplify';
import { putDocumentFileUpload } from '../../../../../graphql/queries';
import { vaultStore } from "../../store/VaultStore";
import Alerts from "../../../../../elements/v2/Alerts/Alerts";
import { useStore } from '../../../../../hooks';
import { StatusFilterEnum } from '../../enums/StatusFilterEnum';
import { AttachFile } from '@mui/icons-material';
const DragAndDrop = ({ onDrop, onClose, onUploadSuccess, customerId, folderId }) => {
  const { authStore } = useStore();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    const handleGlobalDragLeave = (e) => {
      if (!e.relatedTarget && !isUploading) {
        setIsDragging(false);
        if (!selectedFile) {
          onClose();
        }
      }
    };

    document.addEventListener('dragleave', handleGlobalDragLeave);
    return () => {
      document.removeEventListener('dragleave', handleGlobalDragLeave);
    };
  }, [onClose, selectedFile, isUploading]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDragging(false);
      if (!selectedFile && !isUploading) {
        onClose();
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = [...e.dataTransfer.files];
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile({
        file: file,
        name: file.name,
        file_type: file.type || 'application/octet-stream'
      });
      onDrop(files);
    }
  };

  const handleUpload = async () => {
    if (selectedFile && customerId && folderId) {
      setIsUploading(true);
      try {
        const input = {
          customer_id: customerId,
          folder_id: folderId,
          created_by: authStore.userId,
          status: StatusFilterEnum.ACTIVE,
          name: selectedFile.name,
          file_type: selectedFile.file_type
        };

        const response = await API.graphql({
          query: putDocumentFileUpload,
          variables: { input },
          authMode: 'AMAZON_COGNITO_USER_POOLS'
        });

        if (response.data.putDocumentFileUpload.statusCode === "200") {
          const responseBody = JSON.parse(response.data.putDocumentFileUpload.body);
          const uploadUrl = responseBody.url;

          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: selectedFile.file,
            headers: {
              'Content-Type': selectedFile.file_type
            }
          });

          if (uploadResponse.ok) {
            if (onUploadSuccess) {
              await onUploadSuccess();
            }
            
            setShowSuccessAlert(true);
            setSelectedFile(null);
            onClose();
          } else {
            throw new Error('Failed to upload file to S3');
          }
        } else {
          throw new Error('Failed to get upload URL');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        vaultStore.error = {
          show: true,
          severity: 'error',
          title: 'Error uploading file',
          message: error.message
        };
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    onClose();
  };

  if (selectedFile) {
    return (
      <DragAndDropContainer hasFile={true}>
        {showSuccessAlert && (
          <Alerts
            severity="success"
            title="Document uploaded successfully"
            message="Your document has been successfully uploaded to the vault."
            onClose={() => setShowSuccessAlert(false)}
          />
        )}
        <DragAndDropContent>
          <AttachFile />
          <FileName>{selectedFile.name}</FileName>
          <FileType>{selectedFile.file_type}</FileType>
          <ButtonContainer>
            <RoundedButton
              kind="blue"
              onClick={handleUpload}
              disabled={isUploading}
              data-testid="upload-button"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </RoundedButton>
            <RoundedButton
              kind="confirm"
              outline={true}
              onClick={handleCancel}
              disabled={isUploading}
              data-testid="cancel-button"
            >
              Cancel
            </RoundedButton>
          </ButtonContainer>
        </DragAndDropContent>
      </DragAndDropContainer>
    );
  }

  return (
    <DragAndDropContainer
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      isDragging={isDragging}
      hasFile={false}
      data-testid="drag-drop-container"
    >
      {showSuccessAlert && (
        <Alerts
          severity="success"
          title="Document uploaded successfully"
          message="Your document has been successfully uploaded to the vault."
          onClose={() => setShowSuccessAlert(false)}
        />
      )}
      <DragAndDropContent>
        <h3>No documents uploaded yet</h3>
        <p>Drop your file here or click to upload</p>
      </DragAndDropContent>
    </DragAndDropContainer>
  );
};

export default DragAndDrop; 